import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "src/@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "src/@/components/ui/select";
import { Card, CardHeader, CardContent } from "src/@/components/ui/card";
import v2_actionConfigCrm from "src/actions/v2/crm/v2_actionConfigCrm";
import useAuth from "src/hooks/useAuth";
import { Input } from "src/@/components/ui/input";
import getActionListaUsuariosByClienteId from "src/actions/usuarios/getActionListaUsuariosByClienteId";
import { MultiSelect } from "src/@/components/ui/multi-select";

const CardNewLead = ({ block, changeBlock }) => {
  const { t } = useTranslation(['automation', 'common']);
  const { cliente } = useAuth();
  const [selectedPipe, setSelectedPipe] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [configCrm, setConfigCrm] = useState(null);
  const [isSelectingPipe, setIsSelectingPipe] = useState(false);
  const [qualificationValue, setQualificationValue] = useState(3);
  const [users, setUsers] = useState([]);

  async function getUsers() {
    try {
      const response = await getActionListaUsuariosByClienteId(cliente._id);
      setUsers(response.data.usuarios);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getUsers();
  }, []);

  let usersFormatted = users?.map((user) => {
    return { value: user._id, label: user.nomeFantasia }
  });

  const labels = {
    1: t('crm.qualification.noInterest', 'Sem interesse'),
    2: t('crm.qualification.cold', 'Frio'),
    3: t('crm.qualification.warm', 'Morno'),
    4: t('crm.qualification.warming', 'Esquentando'),
    5: t('crm.qualification.hot', 'Quente'),
  };

  async function buscarConfigCrm(querys) {
    try {
      const result = await v2_actionConfigCrm(cliente._id, querys);
      if (result.data.data) {
        setConfigCrm(result.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (block?.data?.pipelineId) {
      let querys = `?pipeline_id=${block?.data?.pipelineId}&cliente_id=${cliente._id}`;
      buscarConfigCrm(querys);
      setIsSelectingPipe(true);
    } else {
      let querys = `?cliente_id=${cliente._id}`;
      buscarConfigCrm(querys);
    }
  }, [])

  return (
    <div className="w-full">
      <div className="space-y-4">
        {/* Pipe Selection */}
        <div>
          <label className="block text-sm font-medium">{t('newLead.nameLabel', 'Nome do lead')}</label>
          <Input
            value={block?.data?.pipelineName}
            onChange={(e) => {
              let event = { name: "pipelineName", value: e.target.value }
              changeBlock(event)
            }}
            type="text"
            className="w-full"
            placeholder={t('newLead.nameLabel', 'Nome do lead')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium">{t('newLead.funnelLabel', 'No funil')}</label>
          <Select
            value={block?.data?.pipelineId}
            onValueChange={e => {
              let querys = `?pipeline_id=${e}&cliente_id=${cliente._id}`;
              buscarConfigCrm(querys);
              setIsSelectingPipe(true);
              let event = { name: "pipelineId", value: e }
              changeBlock(event)
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={selectedPipe} />
            </SelectTrigger>
            <SelectContent>
              {configCrm?.pipelines.map((pipe) => (
                <SelectItem key={pipe._id} value={pipe._id}>
                  {pipe.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Phase Selection */}
        {isSelectingPipe && (
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium">{t('newLead.stageLabel', 'Estágio')}</label>
              <Select
                value={block?.data?.stageId}
                onValueChange={e => {
                  let event = { name: "stageId", value: e }
                  changeBlock(event)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={block?.data?.stageId ? block?.data?.stageId : t('newLead.selectPlaceholder', 'Selecione...')} />
                </SelectTrigger>
                <SelectContent>
                  {configCrm?.stages.map((stage) => (
                    <SelectItem key={stage._id} value={stage._id}>
                      {stage.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium">{t('crm.originLabel', 'Origem')}</label>
              <Select
                value={block?.data?.originId}
                onValueChange={e => {
                  let event = { name: "originId", value: e }
                  changeBlock(event)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={block?.data?.originId} />
                </SelectTrigger>
                <SelectContent>
                  {configCrm?.origens.map((pipe) => (
                    <SelectItem key={pipe._id} value={pipe._id}>
                      {pipe.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium">{t('crm.campaignLabel', 'Campanha')}</label>
              <Select
                value={block?.data?.campanhaId}
                onValueChange={e => {
                  let event = { name: "campanhaId", value: e }
                  changeBlock(event)
                }}
              >
                <SelectTrigger className="w-full h-9 border-gray-200 hover:border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900 text-sm">
                  <SelectValue placeholder={t('crm.selectCampaignPlaceholder', 'Selecione a campanha')} />
                </SelectTrigger>
                <SelectContent>
                  {configCrm?.campanhas?.map((campanha) => (
                    <SelectItem key={campanha._id} value={campanha._id}>
                      {campanha.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">{t('crm.qualificationLabel', 'Qualificação')}</label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  step="1"
                  value={block?.data?.qualifyLead ? block?.data?.qualifyLead : 1}
                  onChange={(e) => {
                    let event = { name: "qualifyLead", value: e.target.value }
                    changeBlock(event)
                  }}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium">{t('labels.user', { ns: 'common', defaultValue: 'Usuário' })}</label>
              <MultiSelect
                options={usersFormatted}
                onValueChange={(e) => {
                  let event = { name: "leadUsersAssign", value: e }
                  changeBlock(event);
                }}
                defaultValue={block?.data?.leadUsersAssign}
                placeholder={t('newLead.selectUsersPlaceholder', 'Selecionar usuarios')}
                variant="inverted"
                animation={2}
                maxCount={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium">{t('newLead.leadValueLabel', 'Valor do lead')}</label>
              <Input
                value={block?.data?.valorLead}
                onChange={(e) => {
                  let event = { name: "valorLead", value: e.target.value }
                  changeBlock(event)
                }}
                type="text"
                className="w-full"
                placeholder={t('labels.value', { ns: 'common', defaultValue: 'Valor' })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardNewLead;
